// src/careJobs/components/LocationSection.tsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Autocomplete from "@mui/material/Autocomplete";

/** ---------- Types (minimal) ---------- */
export type WorkLocationMode = "On-site" | "Hybrid" | "Remote";

export interface LocationRule {
  mode: WorkLocationMode;
  onsiteDaysPerWeek: number | null; // for Hybrid only
  locations: string[];              // free-typed list: "Seattle, WA", "123 Main St, Seattle"
}

export const DEFAULT_LOCATION_RULE: LocationRule = {
  mode: "On-site",
  onsiteDaysPerWeek: 3,
  locations: [],
};

/** Build a short, human-readable summary */
export function buildLocationSummary(rule: LocationRule): string {
  const parts: string[] = [];

  if (rule.mode === "Hybrid") {
    const days = rule.onsiteDaysPerWeek ?? 3;
    parts.push(`Hybrid • ~${days} day${days === 1 ? "" : "s"}/week on-site`);
  } else {
    parts.push(rule.mode);
  }

  if (rule.locations.length) {
    parts.push(`Locations: ${rule.locations.join(", ")}`);
  }

  return parts.join(" • ");
}

/** ---------- Component ---------- */
type Props = {
  value?: LocationRule;
  onChange?: (next: LocationRule) => void;
  onGenerateSummary?: (summary: string) => void;
  disabled?: boolean;
  actionLabel?: string;          // default: "Copy & paste into Description"
  businessAddress?: string | null; // ⬅️ NEW: auto-populate for On-site/Hybrid
};

export default function LocationSection({
  value = DEFAULT_LOCATION_RULE,
  onChange,
  onGenerateSummary,
  disabled,
  actionLabel = "Copy & paste into Description",
  businessAddress,
}: Props) {
  const rule = value ?? DEFAULT_LOCATION_RULE;

  /** Small setter */
  const set = <K extends keyof LocationRule>(key: K, v: LocationRule[K]) =>
    onChange?.({ ...rule, [key]: v });

  /** Auto-add business address once when:
   *  - we have a businessAddress
   *  - mode is On-site or Hybrid
   *  - it's not already present in locations
   */
  React.useEffect(() => {
    const addr = (businessAddress ?? "").trim();
    if (!addr) return;
    const onsiteOrHybrid = rule.mode === "On-site" || rule.mode === "Hybrid";
    if (!onsiteOrHybrid) return;
    if (rule.locations.includes(addr)) return;
    // If no user-entered locations yet, seed with business address.
    if (rule.locations.length === 0) {
      onChange?.({ ...rule, locations: [addr] });
    }
    // If you prefer to ALWAYS include it for On-site/Hybrid, uncomment:
    // else onChange?.({ ...rule, locations: [...rule.locations, addr] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessAddress, rule.mode]);

  const summary = buildLocationSummary(rule);

  const copyAndNotify = async () => {
    try {
      await navigator.clipboard.writeText(summary);
    } catch { /* ignore if blocked */ }
    try {
      window.dispatchEvent(
        new CustomEvent("plany:add-location-summary", { detail: { summary } })
      );
    } catch { /* SSR/old browsers */ }
    onGenerateSummary?.(summary);
  };

  const isHybrid = rule.mode === "Hybrid";

  return (
    <Accordion defaultExpanded disableGutters sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Location</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          {/* Mode (On-site / Hybrid / Remote) */}
          <FormControl component="fieldset">
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Work arrangement
            </Typography>
            <RadioGroup
              row
              value={rule.mode}
              onChange={(e) => set("mode", e.target.value as WorkLocationMode)}
            >
              <FormControlLabel value="On-site" control={<Radio disabled={disabled} />} label="On-site" />
              <FormControlLabel value="Hybrid"  control={<Radio disabled={disabled} />} label="Hybrid" />
              <FormControlLabel value="Remote"  control={<Radio disabled={disabled} />} label="Remote" />
            </RadioGroup>
          </FormControl>

          {/* Hybrid: days on site (compact) */}
          {isHybrid && (
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <InputLabel id="onsite-days-label">On-site days/week</InputLabel>
              <Select
                labelId="onsite-days-label"
                label="On-site days/week"
                value={rule.onsiteDaysPerWeek ?? 3}
                onChange={(e) => set("onsiteDaysPerWeek", Number(e.target.value))}
                disabled={disabled}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Minimal locations input: chips + free typing.
              Auto-seeds with businessAddress for On-site/Hybrid (see useEffect). */}
          <Autocomplete
            multiple
            freeSolo
            disableCloseOnSelect
            options={[
              "Seattle, WA",
              "Bellevue, WA",
              "Redmond, WA",
              "Kirkland, WA",
              "Tacoma, WA",
            ]}
            value={rule.locations}
            onChange={(_, newVal) => set("locations", newVal as string[])}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Location(s)"
                placeholder={
                  businessAddress?.trim()
                    ? `Press Enter to add more (auto: ${businessAddress})`
                    : "Type and press Enter (e.g., “Seattle, WA”)"
                }
              />
            )}
            disabled={disabled}
          />

          {/* Summary + single action */}
          <Alert
            severity="success"
            icon={false}
            sx={{
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={copyAndNotify}
                disabled={disabled}
              >
                {actionLabel}
              </Button>
            }
          >
            {summary}
          </Alert>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
